// Arranging the League page, and where a league opens (scenario L3,
// PLAN-2026-10-02-your-account-follows-you.md §6, B-247).
//
// Both live at the foot of the League page, quiet, and nothing prompts
// anybody to use them: discoverable and never required. Both are the
// account's, so they follow the person to every device.
//
// THE ARRANGING LIST rather than dragging the page's own panels about: on a
// phone a list with ↑ and ↓ is the thing a thumb can do, and it reads the
// same at every width. Under a pointer a row can be dragged as well.

import { useState } from 'react';
import { DETAIL_LABEL, atLeast } from '@fsp/theme';
import type { OpensOn } from '@fsp/domain';
import { useDetail } from '../../data/detail';
import { Panel } from '../../kit/Panel';
import { Button, Caption, Choice, IconButton, Line, Stack, Text } from '../../kit/ui';
import {
  LEAGUE_PAGE_REGIONS,
  USUAL_ARRANGEMENT,
  droppedOn,
  isUsual,
  moved,
  withHidden,
  type Arrangement,
} from './leaguePageLayout';

/** Where a league can open, named as the tabs and the rail name them. */
const OPENS_ON: { value: OpensOn; label: string }[] = [
  { value: 'team', label: 'My Team' },
  { value: 'matchup', label: 'Game Day' },
  { value: 'scores', label: 'Scoreboard' },
  { value: 'players', label: 'Players' },
  { value: 'league', label: 'League' },
];

/** The foot of the League page: where this league opens, and Arrange. */
export function LeaguePageFoot({
  opensOn,
  onOpensOn,
  onArrange,
}: {
  opensOn: OpensOn;
  onOpensOn: (where: OpensOn) => void;
  onArrange: () => void;
}) {
  return (
    <div className="fsp-league-foot">
      <div className="fsp-league-foot-opens">
        <Choice
          label="This league opens on"
          options={OPENS_ON}
          value={opensOn}
          onChange={(v) => {
            const where = OPENS_ON.find((o) => o.value === v)?.value;
            if (where !== undefined && where !== opensOn) onOpensOn(where);
          }}
          searchable={false}
          required
        />
      </div>
      <Button variant="quiet" size="sm" icon="list" onClick={onArrange}>
        Arrange this page
      </Button>
    </div>
  );
}

const labelOf = (id: string) => LEAGUE_PAGE_REGIONS.find((r) => r.id === id)?.label ?? id;

/** The page while it is being arranged. */
export function ArrangeLeaguePage({
  initial,
  onDone,
  onCancel,
  labels,
}: {
  initial: Arrangement;
  onDone: (arrangement: Arrangement) => void;
  onCancel: () => void;
  /** A section named as the page names it now: Playoffs, in the playoffs (B-408). */
  labels?: Readonly<Record<string, string>>;
}) {
  const nameOf = (id: string) => labels?.[id] ?? labelOf(id);
  const [a, setA] = useState(initial);
  const [dragging, setDragging] = useState<string | null>(null);
  const detail = useDetail();
  const shown = a.order.filter((id) => !a.hidden.includes(id));
  const hidden = a.order.filter((id) => a.hidden.includes(id));

  /** A region this level leaves out says so, so a move is not a mystery. */
  const note = (id: string) => {
    const from = LEAGUE_PAGE_REGIONS.find((r) => r.id === id)?.from ?? null;
    return from !== null && !atLeast(detail, from) ? `Shows from ${DETAIL_LABEL[from]}` : null;
  };

  return (
    <div className="fsp-screen">
      <div className="fsp-widgets fsp-account-column">
        <Panel title="Arrange the League page">
          <Stack gap={3}>
            <Text tone="secondary" block>
              One order for every league and every device. A wider screen fills its columns from it,
              the first ones on the left.
            </Text>
            <ol className="fsp-arrange-list">
              {shown.map((id, i) => (
                <li
                  key={id}
                  className={`fsp-arrange-row${dragging === id ? ' fsp-arrange-dragging' : ''}`}
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.setData('text/plain', id);
                    e.dataTransfer.effectAllowed = 'move';
                    setDragging(id);
                  }}
                  onDragEnd={() => setDragging(null)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (dragging !== null) setA(droppedOn(a, dragging, id));
                    setDragging(null);
                  }}
                >
                  <span className="fsp-arrange-grip" aria-hidden>
                    ⋮⋮
                  </span>
                  <div className="fsp-grow">
                    <Text weight="strong" block>
                      {nameOf(id)}
                    </Text>
                    {note(id) !== null ? (
                      <Text size="xs" tone="secondary" block>
                        {note(id)}
                      </Text>
                    ) : null}
                  </div>
                  <IconButton
                    icon="up"
                    label={`Move ${nameOf(id)} up`}
                    variant="quiet"
                    size="sm"
                    disabled={i === 0}
                    onClick={() => setA(moved(a, id, -1))}
                  />
                  <IconButton
                    icon="down"
                    label={`Move ${nameOf(id)} down`}
                    variant="quiet"
                    size="sm"
                    disabled={i === shown.length - 1}
                    onClick={() => setA(moved(a, id, 1))}
                  />
                  <Button
                    size="xs"
                    variant="quiet"
                    label={`Hide ${nameOf(id)}`}
                    onClick={() => setA(withHidden(a, id, true))}
                  >
                    Hide
                  </Button>
                </li>
              ))}
            </ol>
            {hidden.length > 0 ? (
              <Stack gap={1}>
                <Caption>Hidden</Caption>
                <ol className="fsp-arrange-list">
                  {hidden.map((id) => (
                    <li key={id} className="fsp-arrange-row fsp-arrange-hidden">
                      <div className="fsp-grow">
                        <Text block>{nameOf(id)}</Text>
                      </div>
                      <Button
                        size="xs"
                        variant="quiet"
                        label={`Show ${nameOf(id)}`}
                        onClick={() => setA(withHidden(a, id, false))}
                      >
                        Show
                      </Button>
                    </li>
                  ))}
                </ol>
              </Stack>
            ) : null}
            <Line spread>
              <Button
                variant="quiet"
                size="sm"
                icon="undo"
                disabled={isUsual(a)}
                onClick={() => setA(USUAL_ARRANGEMENT)}
              >
                Reset
              </Button>
              <Line>
                <Button variant="quiet" size="sm" onClick={onCancel}>
                  Cancel
                </Button>
                <Button variant="primary" size="sm" onClick={() => onDone(a)}>
                  Done
                </Button>
              </Line>
            </Line>
          </Stack>
        </Panel>
      </div>
    </div>
  );
}
